import { CommonModule, DOCUMENT } from "@angular/common";
import { Component, HostListener, inject, Input, Type } from "@angular/core";
import { NxCopytextModule } from "@aposin/ng-aquila/copytext";
import { NxFooterModule } from "@aposin/ng-aquila/footer";
import { NxGridModule } from "@aposin/ng-aquila/grid";
import { ItHelpdeskModalComponent } from "@modules/modals/it-helpdesk-modal/it-helpdesk-modal.component";
import { PrivacyPrinciplesModalComponent } from "@modules/modals/privacy-principles-modal/privacy-principles-modal.component";
import { TermsOfUseModalComponent } from "@modules/modals/terms-of-use-modal/terms-of-use-modal.component";
import { TranslateModule } from "@ngx-translate/core";
import { ModalService } from "@services/modal.service";
import { SharedModule } from "@shared/shared.module";

const MODAL_CONFIG = { disableClose: false, autoFocus: "dialog" };
const SCROLL_BUTTON_THRESHOLD_PERCENT = 80;

@Component({
  selector: "app-footer",
  standalone: true,
  imports: [
    CommonModule,
    NxGridModule,
    NxFooterModule,
    NxCopytextModule,
    SharedModule,
    TranslateModule,
  ],
  templateUrl: "./footer.component.html",
  styleUrl: "./footer.component.scss",
})
export class FooterComponent {
  private readonly document = inject(DOCUMENT);
  private readonly modalService = inject(ModalService);

  @Input() public isLoginPage = false;

  protected showScrollButton = false;
  protected readonly currentYear = new Date().getFullYear();

  private isAutoScrollingToTop = false;

  @HostListener("window:scroll")
  protected onWindowScroll(): void {
    const view = this.document.defaultView;

    if (!view) {
      this.showScrollButton = false;
      return;
    }

    const scrollTop =
      view.pageYOffset ||
      this.document.documentElement.scrollTop ||
      this.document.body.scrollTop ||
      0;

    if (this.isAutoScrollingToTop) {
      this.showScrollButton = false;

      if (scrollTop <= 0) {
        this.isAutoScrollingToTop = false;
      }

      return;
    }

    if (scrollTop <= 0) {
      this.showScrollButton = false;
      return;
    }

    const documentHeight = Math.max(
      this.document.body.scrollHeight,
      this.document.documentElement.scrollHeight,
    );
    const scrollableHeight = documentHeight - view.innerHeight;

    if (scrollableHeight <= 0) {
      this.showScrollButton = false;
      return;
    }

    const scrollPercentage = (scrollTop / scrollableHeight) * 100;
    this.showScrollButton = scrollPercentage >= SCROLL_BUTTON_THRESHOLD_PERCENT;
  }

  protected toggleItHelpdesk(): void {
    this.openModal(ItHelpdeskModalComponent);
  }

  protected togglePrivacyPrinciples(): void {
    this.openModal(PrivacyPrinciplesModalComponent);
  }

  protected toggleTermsOfUse(): void {
    this.openModal(TermsOfUseModalComponent);
  }

  protected scrollToTop(): void {
    this.isAutoScrollingToTop = true;
    this.showScrollButton = false;
    this.document.defaultView?.scrollTo({ top: 0, behavior: "smooth" });
  }

  private openModal(component: Type<unknown>): void {
    this.modalService.openDialog(component, "", MODAL_CONFIG);
  }
}
